import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";

import { ActionButton, FormDialog, InputField } from "../v3";
import { AccountService } from "../../services";
import { accountValidationRules } from "../../pages/account/validation";

export const AddEditAccount = ({ open, onClose, accountId, onSuccess }) => {
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [accountDetails, setAccountDetails] = useState({
    name: "",
    initialBalance: 0,
    meta: {
      ifscCode: "",
      branchName: "",
      accountNumber: "",
    },
    type: "",
  });

  useEffect(() => {
    if (open && accountId) {
      fetchAccountData(accountId);
    } else if (open && !accountId) {
      setAccountDetails({
        name: "",
        initialBalance: 0,
        meta: {
          ifscCode: "",
          branchName: "",
          accountNumber: "",
        },
        type: "",
      });
    }

    fetchData();
  }, [open, accountId]);

  const fetchData = async () => {
    try {
      const response = await AccountService.getAccountTypes();
      const types = response?.data?.map((type, index) => ({
        id: index + 1,
        label: type,
        value: type,
      }));
      setAccountTypes(types);
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const fetchAccountData = async (accountId) => {
    try {
      const response = await AccountService.getAccountById(accountId);
      setAccountDetails({
        name: response?.data?.name || "",
        type: response?.data?.type || "",
        initialBalance: Number(response?.data?.initialBalance) || 0,
        meta: {
          ifscCode: response?.data?.meta?.ifscCode || "",
          branchName: response?.data?.meta?.branchName || "",
        },
      });
    } catch (error) {
      console.error("🚀 ~ fetchAccountData ~ error:", error);
    }
  };

  const handleClearForm = () => {
    setAccountDetails({
      name: "",
      initialBalance: 0,
      meta: {
        ifscCode: "",
        branchName: "",
        accountNumber: "",
      },
      type: "",
    });
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;

    if (name === "ifscCode" || name === "branchName" || name === "accountNumber") {
      setAccountDetails((prev) => ({
        ...prev,
        meta: {
          ...prev.meta,
          [name]: value,
        },
      }));
    } else {
      setAccountDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const handleSaveAccount = async () => {
    setErrors({});
    const errors = accountValidationRules(accountDetails);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    try {
      setLoading(true);
      accountDetails.initialBalance = Number(accountDetails.initialBalance);
      if (accountId) {
        await AccountService.updateAccount(accountId, accountDetails);
        toast.success("Account updated successfully!");
      } else {
        await AccountService.createAccount(accountDetails);
        toast.success("Account created successfully!");
      }
      if (onSuccess) onSuccess();
      handleCloseModal();
    } catch (error) {
      let errorMessage = `Error ${accountId ? "updating" : "adding"} account`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });
      console.error("🚀 ~ handleSaveAccount ~ error:", error);
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };

  return (
    <FormDialog
      open={open}
      title={accountId ? "Edit Account" : "Add Account"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveAccount} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Account Name"
            name="name"
            value={accountDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <InputField
            required
            select={true}
            selectOptions={accountTypes}
            label="Type"
            name="type"
            value={accountDetails.type}
            onChange={updateDetailsState}
            error={!!errors.type}
            helperText={errors.type}
            fullWidth
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="IFSC Code"
            name="ifscCode"
            value={accountDetails.meta.ifscCode}
            onChange={updateDetailsState}
            error={accountDetails.type === "BANK" && !!errors.meta?.ifscCode}
            helperText={accountDetails.type === "BANK" ? errors.meta?.ifscCode : ""}
            fullWidth
            disabled={accountDetails.type !== "BANK"}
          />
          <InputField
            label="Branch"
            name="branchName"
            value={accountDetails.meta.branchName}
            onChange={updateDetailsState}
            error={accountDetails.type === "BANK" && !!errors.meta?.branchName}
            helperText={accountDetails.type === "BANK" ? errors.meta?.branchName : ""}
            fullWidth
            disabled={accountDetails.type !== "BANK"}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            type="number"
            label="Account Number"
            name="accountNumber"
            value={accountDetails.meta.accountNumber}
            onChange={updateDetailsState}
            error={accountDetails.type === "BANK" && !!errors.meta?.accountNumber}
            helperText={accountDetails.type === "BANK" ? errors.meta?.accountNumber : ""}
            fullWidth
            disabled={accountDetails.type !== "BANK"}
          />
          <InputField
            label="Initial Balance"
            name="initialBalance"
            type="number"
            value={accountDetails.initialBalance}
            onChange={updateDetailsState}
            error={!!errors.initialBalance}
            helperText={errors.initialBalance}
            fullWidth
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
